import { IcoTrash16 } from '@onefootprint/icons';
import { cx } from 'class-variance-authority';
import { type ReactNode, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { FieldKeyError } from '../../store/field-key';
import { useBuilder } from '../../store/provider';
import type { Field } from '../../store/types';
import { fieldRowId } from '../../store/utils';
import { type SchemaPatch, toSchemaEditorState } from '../../store/wire';
import FieldKeyErrorMessage from '../field-key-error';
import { ClickToReveal, InlineText, ProsePreview } from '../shared/inline-edit';
import MarkdownField from './markdown-field';
import { SchemaDetailsEditor, SchemaTypeSelect } from './schema';
type FieldRowProps = {
  taskId: string;
  field: Field;
  autofocusName?: boolean;
  keyError?: FieldKeyError;
  children?: ReactNode;
  readOnly?: boolean;
};
const FieldRow = ({
  taskId,
  field,
  autofocusName,
  keyError,
  children,
  readOnly
}: FieldRowProps) => {
  const {
    t
  } = useTranslation('agents', {
    keyPrefix: 'agentBuilder.canvas.task.field'
  });
  const {
    state,
    actions,
    isReadOnly
  } = useBuilder();
  const canEdit = !isReadOnly && !readOnly;
  const isHighlighted = state.highlightedId === fieldRowId(field.id);
  const autoOpenEditorRef = useRef(false);
  const schemaState = toSchemaEditorState(field);
  const updateSchema = (patch: SchemaPatch) => {
    actions.tasks.updateField(taskId, field.id, patch);
  };
  return <div data-id={fieldRowId(field.id)} className={cx('group/field -mx-1 flex flex-col gap-1 rounded px-1 py-1.5 transition-colors duration-300', isHighlighted ? 'bg-active' : canEdit && 'hover:bg-hover')}>
      <div className="flex items-center gap-2">
        <InlineText value={field.name} prefix="outputs." mono ariaLabel={t($ => $.nameAria)} placeholder={t($_0 => $_0.namePlaceholder)} canEdit={canEdit} autoEdit={autofocusName} hasError={!!keyError} onChange={next => actions.tasks.updateField(taskId, field.id, {
        name: next
      })} />
        <SchemaTypeSelect id={`field-${field.id}-type`} ariaLabel={t($_1 => $_1.typeAria)} schema={schemaState} canEdit={canEdit} onChange={(patch_0, autoOpenEditor) => {
        autoOpenEditorRef.current = autoOpenEditor;
        updateSchema(patch_0);
      }} />
        {canEdit && <button type="button" aria-label={t($_2 => $_2.removeAria)} onClick={() => actions.tasks.removeField(taskId, field.id)} className="ml-auto flex h-6 w-6 shrink-0 items-center max-md:size-8 justify-center rounded-(--icon-button-border-radius-tiny) max-md:rounded-(--icon-button-border-radius) transition-opacity hover:bg-hover-control md:opacity-0 md:group-hover/field:opacity-100">
            <IcoTrash16 color="tertiary" />
          </button>}
      </div>

      <FieldKeyErrorMessage error={keyError} />

      <ClickToReveal canEdit={canEdit} ariaLabel={t($_3 => $_3.prompt.ariaLabel)} className="px-1 py-0.5" display={<ProsePreview value={field.prompt} placeholder={t($_4 => $_4.prompt.placeholder)} canEdit={canEdit} />}>
        {() => <MarkdownField label={t($_5 => $_5.prompt.label)} hint={t($_6 => $_6.prompt.hint)} value={field.prompt} onChange={prompt => actions.tasks.updateField(taskId, field.id, {
        prompt
      })} mentionNodeId={taskId} />}
      </ClickToReveal>

      <SchemaDetailsEditor schema={schemaState} canEdit={canEdit} autoOpen={autoOpenEditorRef.current} onChange={updateSchema}>
        {children}
      </SchemaDetailsEditor>
    </div>;
};
export default FieldRow;